// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Page routing
//
//  Copyright:    © 2025 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   20.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   12.03.25 Sean Flook          GMSCM-1 Code required for managing the configuration and cluster documents.
//    003   05.02.26 Sean Flook         GMSCM-12 Added Application and Organisation functionality.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import { ReactElement, useEffect } from "react";
import { Routes, Route } from "react-router-dom";

import useUserQueryStore from "./context/UserStore";

import { hasLoginExpired, loginStorageName, tokenStorageName, userStorageName } from "./utils/HelperUtils";

import DocumentsPage from "./pages/DocumentsPage";
import ClusterPage from "./pages/ClusterPage";
import ApplicationPage from "./pages/ApplicationPage";
import OrganisationPage from "./pages/OrganisationPage";

export const DocumentsRoute = "/documents";
export const ClustersRoute = "/clusters";
export const ApplicationsRoute = "/applications";
export const OrganisationsRoute = "/organisations";

const PageRouting = (): ReactElement => {
  const expiredToken = useUserQueryStore((s) => s.expiredToken);
  const setLoginDetails = useUserQueryStore((s) => s.setLoginDetails);
  const setCurrentUser = useUserQueryStore((s) => s.setCurrentUser);

  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  useEffect(() => {
    if (localStorage.getItem(loginStorageName) !== null) {
      const storedLogin: string | null = localStorage.getItem(loginStorageName);
      if (storedLogin) {
        const savedLogin = JSON.parse(storedLogin);
        if (savedLogin.expiry && hasLoginExpired(savedLogin.expiry)) {
          expiredToken();
          localStorage.removeItem(loginStorageName);
          localStorage.removeItem(tokenStorageName);
          localStorage.removeItem(userStorageName);
        } else {
          setLoginDetails(savedLogin);

          if (localStorage.getItem(userStorageName) !== null && !currentUser) {
            const storedUser: string | null = localStorage.getItem(userStorageName);
            if (storedUser) {
              const savedUser = JSON.parse(storedUser);
              setCurrentUser(savedUser);
            }
          }
        }
      }
    }
  }, [expiredToken, setCurrentUser, setLoginDetails, currentUser]);

  return currentUser ? (
    <Routes>
      <Route path={DocumentsRoute} element={<DocumentsPage />} />
      <Route path={ClustersRoute} element={<ClusterPage />} />
      <Route path={ApplicationsRoute} element={<ApplicationPage />} />
      <Route path={OrganisationsRoute} element={<OrganisationPage />} />
      <Route path="*" element={<DocumentsPage />} />
    </Routes>
  ) : (
    <></>
  );
};

export default PageRouting;
